import { useCallback, useRef, useState } from "react";
import { useCreatePortfolioLeadMutation } from "web_myplann/Api/Portfolio/useCreatePortfolioLeadMutation";
import { useRecordAlertBoxActionMutation } from "web_myplann/Api/Portfolio/useRecordAlertBoxActionMutation";
import { useOpenCalculator } from "web_myplann/Calculators/Hooks/useOpenCalculator";
import { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import {
	AlertBoxActionEnum,
	AlertBoxActionStateEnum,
	type PortfolioAlertBoxDTO,
	type PortfolioDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";

/**
 * Dispatches Event Alert CTA actions.
 *
 * - "Talk to agent" opens the shared {@link ContactAgentDialog}; confirming it
 *   creates an advisor lead for the alert's contract.
 * - "Compare rates" opens the refinancing calculator prefilled from the
 *   contract: the web one for pilot users, otherwise the native one; older
 *   shells without that screen get the calculator list.
 * - The insurance actions (renew / recalculate / sign new travel) have no
 *   arranger ("sjednávač") destination yet, so per spec they record the fallback
 *   "Opened" state on click, surfacing the "Naposledy otevřeno" subtext.
 *
 * "Check conditions" has no defined target yet, so it stays an intentional
 * no-op (SEAM).
 */
const INSURANCE_ACTIONS: ReadonlySet<AlertBoxActionEnum> = new Set([
	AlertBoxActionEnum.RenewInsurance,
	AlertBoxActionEnum.RecalculateInsurance,
	AlertBoxActionEnum.SignNewTravelInsurance,
]);

export const useEventActions = (
	contractId: number,
	portfolio?: PortfolioDetailResponseDTO | null,
) => {
	const [selectedAlert, setSelectedAlert] =
		useState<PortfolioAlertBoxDTO | null>(null);
	const { mutate: createLead, isPending: isLeadPending } =
		useCreatePortfolioLeadMutation();
	const { mutate: recordAction } = useRecordAlertBoxActionMutation();
	const { openCalculator, openRefinancingCalculator } = useOpenCalculator();

	// Guards against a double tap firing a second lead before the pending state
	// renders, independent of state batching; a failed request re-arms it so
	// the user can retry from the same dialog.
	const leadCreatedRef = useRef(false);

	const handleAction = (
		action: AlertBoxActionEnum,
		alert: PortfolioAlertBoxDTO,
	) => {
		if (action === AlertBoxActionEnum.TalkToAgent) {
			leadCreatedRef.current = false;
			setSelectedAlert(alert);
			return;
		}
		if (action === AlertBoxActionEnum.CompareRates) {
			void (portfolio
				? openRefinancingCalculator(portfolio)
				: openCalculator(CalculatorType.Refinancing));
			return;
		}
		if (INSURANCE_ACTIONS.has(action)) {
			recordAction({
				contractId,
				eventType: alert.eventType,
				eventDate: alert.eventDate,
				action,
				state: AlertBoxActionStateEnum.Opened,
			});
		}
	};

	const closeContactDialog = useCallback(() => setSelectedAlert(null), []);

	const confirmLead = useCallback(
		(onSuccess: () => void) => {
			if (!selectedAlert || leadCreatedRef.current) {
				return;
			}
			leadCreatedRef.current = true;
			createLead(
				{
					contractId,
					eventType: selectedAlert.eventType,
					eventDate: selectedAlert.eventDate,
				},
				{
					onSuccess,
					onError: () => {
						leadCreatedRef.current = false;
					},
				},
			);
		},
		[contractId, createLead, selectedAlert],
	);

	return {
		handleAction,
		contactDialogOpen: selectedAlert !== null,
		closeContactDialog,
		confirmLead,
		isLeadPending,
	};
};
